/**
 * localEbmOps — offline-first reads for EBM/fiscal integration data
 *
 * Sibling to localOrderOps.ts rather than bolted onto it (that file is
 * already ~1900 lines). Reads only for now — the write path
 * (localWriteEbmSaleRecord, the atomic counter+insert) is added once the
 * main-process submission flow that calls it exists.
 */

import { ApiResponse } from '@shared/types';

import {
  EbmSaleRecordRow,
  FiscalTaxCategoryRow,
  OutletEbmConfigRow,
} from './schema';
import { psGet, psGetAll } from './ipcBridge';
import { toNumber } from './localOrderOps';

export async function localFetchOutletEbmConfig(
  outletId: string,
): Promise<ApiResponse<OutletEbmConfigRow | null>> {
  try {
    const row = await psGet<OutletEbmConfigRow>(
      `SELECT id, outlet_id, deployment_mode, vsdc_base_url, tin, bhf_id,
              dvc_srl_no, activation_status, last_init_result_cd, last_init_at,
              last_health_check_at, last_health_ok, created_at, updated_at
       FROM outlet_ebm_configs WHERE outlet_id = ? LIMIT 1`,
      [outletId],
    );
    return { success: true, data: row ?? null };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch EBM config',
      data: null,
    };
  }
}

export async function localFetchFiscalTaxCategories(
  countryIso: string,
): Promise<ApiResponse<FiscalTaxCategoryRow[]>> {
  try {
    const rows = await psGetAll<any>(
      `SELECT id, country_iso, code, name, default_rate, is_default, is_active, display_order
       FROM fiscal_tax_categories
       WHERE country_iso = ? AND is_active = 1
       ORDER BY display_order ASC`,
      [countryIso],
    );
    return {
      success: true,
      data: rows.map((r) => ({
        id: r.id,
        country_iso: r.country_iso,
        code: r.code,
        name: r.name,
        default_rate: toNumber(r.default_rate),
        is_default: r.is_default ? 1 : 0,
        is_active: r.is_active ? 1 : 0,
        display_order: toNumber(r.display_order),
      })),
    };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error
          ? err.message
          : 'Failed to fetch fiscal tax categories',
      data: [],
    };
  }
}

export async function localFetchEbmStatusForOrder(
  orderId: string,
): Promise<ApiResponse<EbmSaleRecordRow | null>> {
  try {
    const row = await psGet<EbmSaleRecordRow>(
      `SELECT * FROM ebm_sale_records WHERE order_id = ? ORDER BY created_at DESC LIMIT 1`,
      [orderId],
    );
    return { success: true, data: row ?? null };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to fetch EBM sale record',
      data: null,
    };
  }
}

export interface LatestSettlement {
  id: string;
  payment_method_id: number;
}

/**
 * Resolves the just-created order_settlements row's id + payment_method_id,
 * for handing to ebm:submitSale right after a settlement thunk succeeds.
 * Works for both the online RPC path (localSeedRpcSettlement already
 * mirrored the row locally) and the offline local-writeTransaction path
 * (which generates its own UUID internally and doesn't return it) — one
 * local read covers both. Also covers credit/other settlement's
 * payment_method_id, which those thunks don't otherwise know directly (it's
 * resolved server/local-side by name-matching, not passed in as a param).
 */
export async function localFetchLatestSettlement(
  orderId: string,
): Promise<ApiResponse<LatestSettlement | null>> {
  try {
    const row = await psGet<LatestSettlement>(
      `SELECT id, payment_method_id FROM order_settlements WHERE order_id = ? ORDER BY created_at DESC LIMIT 1`,
      [orderId],
    );
    return { success: true, data: row ?? null };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to resolve settlement',
      data: null,
    };
  }
}

export interface UnclassifiedFiscalItem {
  menu_item_id: string;
  menu_item_name: string;
  missing_tax_category: 0 | 1;
  missing_item_cd: 0 | 1;
  missing_item_cls_cd: 0 | 1;
}

/**
 * Blocking checkout-path check — fully local (no network round trip), called
 * by all 4 settlement thunks before their online/offline branch, only when
 * the outlet is under fiscal obligation (caller checks the cached outlet
 * is_ebm_active flag first).
 */
export async function localCheckOrderFiscalReadiness(
  orderId: string,
): Promise<ApiResponse<UnclassifiedFiscalItem[]>> {
  try {
    const rows = await psGetAll<UnclassifiedFiscalItem>(
      `SELECT DISTINCT mi.id AS menu_item_id, mi.name AS menu_item_name,
              (mi.fiscal_tax_category_id IS NULL) AS missing_tax_category,
              (mi.ebm_item_cd IS NULL OR trim(mi.ebm_item_cd) = '') AS missing_item_cd,
              (mi.ebm_item_cls_cd IS NULL OR trim(mi.ebm_item_cls_cd) = '') AS missing_item_cls_cd
       FROM order_items oi
       JOIN menu_items mi ON mi.id = oi.menu_item_id
       WHERE oi.order_id = ? AND oi.status <> 'CANCELLED'
         AND (mi.fiscal_tax_category_id IS NULL OR mi.ebm_item_cd IS NULL OR trim(mi.ebm_item_cd) = ''
              OR mi.ebm_item_cls_cd IS NULL OR trim(mi.ebm_item_cls_cd) = '')`,
      [orderId],
    );
    return { success: true, data: rows };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to check fiscal readiness',
      data: [],
    };
  }
}
